import styled from 'styled-components';

import { Horizontal, Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { TAB_ACTIVE_BORDER_HEIGHT } from './common';

export type HeaderProps = {
  actions?: JSX.Element;
  breadcrumbs?: JSX.Element;
  tabs?: JSX.Element;
  tabsActions?: JSX.Element;
  filters?: JSX.Element;
  backAction?: JSX.Element;
  title?: JSX.Element;
  selectionHeader?: JSX.Element;
  showSelectionHeader?: boolean;
  hideTabs?: boolean;
};

export const Header = ({
  actions,
  breadcrumbs,
  tabs,
  tabsActions,
  filters,
  backAction,
  title,
  selectionHeader,
  showSelectionHeader = false,
  hideTabs = false,
}: HeaderProps) => {
  const hasTabs = Boolean(tabs) && !hideTabs;
  const hasFilters = Boolean(filters);
  return (
    <Container
      $hasTabs={hasTabs}
      $hasFilters={hasFilters}
      $isSelection={showSelectionHeader}
      id="header"
    >
      {showSelectionHeader ? (
        selectionHeader
      ) : (
        <>
          <MainHeaderContent>
            <Stack space={2} orientation="horizontal" align="center" id="header-left">
              {backAction}
              {title ? (
                <TitleContainer>{title}</TitleContainer>
              ) : breadcrumbs ? (
                <Breadcrumbs>{breadcrumbs}</Breadcrumbs>
              ) : null}
            </Stack>
            <HeaderActions id="header-actions">{actions}</HeaderActions>
          </MainHeaderContent>
          {tabs && !hideTabs && (
            <Horizontal space={4} align="flex-end">
              <TabsContainer id="header-tabs">{tabs}</TabsContainer>
              {tabsActions && <TabsActions id="header-tabs-actions">{tabsActions}</TabsActions>}
            </Horizontal>
          )}
          {hasFilters && (
            <>
              <FiltersSeparator />
              <FiltersContainer>{filters}</FiltersContainer>
            </>
          )}
        </>
      )}
    </Container>
  );
};

const HeaderHeightWithTabs = '7.5rem';
export const HeaderHeightDefault = '5.5rem';

const Container = styled.div<{ $hasTabs: boolean; $hasFilters: boolean; $isSelection: boolean }>(
  ({ $hasTabs, $hasFilters, $isSelection }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    padding: pxToRem(24),
    paddingBottom: $hasTabs || $hasFilters ? '0' : pxToRem(24),
    transition: transitionStandard('min-height'),
    justifyContent: $hasFilters ? 'flex-start' : $hasTabs ? 'space-between' : 'center',
    minHeight: $hasTabs ? HeaderHeightWithTabs : HeaderHeightDefault,
    backgroundColor: $isSelection
      ? theme.color.backgroundSecondary
      : theme.color.backgroundLightDark,
    borderBottom: `${pxToRem(1)} solid ${
      $isSelection ? theme.color.uiTrans10 : theme.color.border10
    }`,
    zIndex: 10,
  })
);

const MainHeaderContent = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  width: '100%',
});

const Breadcrumbs = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  height: '2.5rem',
});

// Reserves the actions' height so a short title centres against them rather than
// sitting high — `MainHeaderContent` is flex-start. `minHeight`, so it still grows
// for a title that wraps.
const TitleContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  minHeight: theme.base.height,
});

// Sits beside TabsContainer rather than within it, so it doesn't scroll and
// fade with the tabs. The padding backs out a tab's trailing gap and active
// border, so this centres on the tab labels rather than on the border beneath
// them.
const TabsActions = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  height: theme.base.height,
  paddingBottom: `calc(${theme.space[4]} + ${TAB_ACTIVE_BORDER_HEIGHT})`,
});

const TabsContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  flexGrow: 1,
  // Scroll the tabs horizontally when they don't all fit, rather than letting
  // them wrap or overflow into the header actions. The scrollbar is hidden so
  // it doesn't sit on top of the short tab row / active underline.
  minWidth: 0,
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  // `overflow-x: auto` also clips vertically, which would cut off a focused
  // tab's focus ring. Add vertical breathing room and pull it back with a
  // matching negative margin so the tab row doesn't visually move.
  paddingTop: pxToRem(6),
  paddingBottom: pxToRem(6),
  marginTop: pxToRem(-6),
  marginBottom: pxToRem(-6),
  // Soften the right edge so an overflowing tab fades out (hinting there's
  // more to scroll) instead of being hard-cut. When the tabs fit, this falls
  // over the trailing margin and is invisible.
  maskImage: `linear-gradient(to right, #000 calc(100% - ${pxToRem(24)}), transparent)`,
  WebkitMaskImage: `linear-gradient(to right, #000 calc(100% - ${pxToRem(24)}), transparent)`,
});

export const HeaderActions = styled.div({
  flexGrow: 1,
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'relative',
  alignItems: 'flex-start',
});

const FiltersSeparator = styled.div({
  width: `calc(100% + ${pxToRem(48)})`,
  height: pxToRem(1),
  backgroundColor: theme.color.border10,
  marginLeft: pxToRem(-24),
  marginRight: pxToRem(-24),
  marginTop: pxToRem(16),
  marginBottom: pxToRem(12),
});

const FiltersContainer = styled.div({
  display: 'flex',
  paddingBottom: pxToRem(16),
});
